import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { isAccountDisabledError, accountDisabledMessage } from '../../http/account-disabled.constant';
import { IamSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import { KLAB_IAM_ENV } from '../../config/micros-env.tokens';
import { EnvMicroKlamIam } from '../../config/env.micro.klam-iam';
import { PasswordRecoveryPort } from '../../../app/ports/iam/password-recovery.port';
import {
  mapSDKVerificationTokenResponseToDomain,
  mapSDKVerificationTokenValidationToDomain,
} from '../../mappers/iam/recovery.mapper';
import {
  RequestRecoveryCodeRequest,
  RequestRecoveryCodeResponse,
  ValidateRecoveryCodeRequest,
  ValidateRecoveryCodeResponse,
  ResetPasswordRequest,
} from '../../../domain/models/session/recovery.model';
import { recoverySdkMock } from '../../mocks/iam/recovery.mock';

const RESET_PASSWORD_POLICY_MESSAGE =
  'Tu contraseña debe:\n' +
  '• Tener al menos 8 caracteres\n' +
  '• Incluir una mayúscula, una minúscula, un número y un símbolo (@ $ ! % * ? &)\n' +
  '• Usar solo letras sin ñ ni tildes, números y los símbolos de la lista\n\n' +
  'Por ejemplo: Password2026@';

type RecoveryMockAction =
  | 'generateVerificationToken'
  | 'validateVerificationToken'
  | 'resetPassword';

@Injectable({ providedIn: 'root' })
export class PasswordRecoveryPortAdapter implements PasswordRecoveryPort {
  constructor(
    private verificationTokensApi: IamSdk.VerificationTokensService,
    private appEvents: AppEventsService,
    @Optional() private toast: CoreUIToastV2Service,
    @Inject(KLAB_IAM_ENV) private env: EnvMicroKlamIam,
  ) { }

  requestRecoveryCode(
    payload: RequestRecoveryCodeRequest,
  ): Observable<RequestRecoveryCodeResponse> {
    this.appEvents.loaderVisiblePublish(true, 'recovery-request-code');

    const normalizedEmail = payload.email.trim().toLowerCase();
    const sdkPayload: IamSdk.GenerateVerificationTokenRequest = {
      email: normalizedEmail,
      tokenType: payload.tokenType as unknown as IamSdk.VerificationTokenType,
    };

    const source$: Observable<IamSdk.VerificationTokenResponse> = this.shouldMock('generateVerificationToken')
      ? of(recoverySdkMock.generateVerificationToken(payload) as any).pipe(delay(450))
      : this.verificationTokensApi.generateVerificationToken(sdkPayload);

    return source$.pipe(
      map((resp: IamSdk.VerificationTokenResponse) =>
        mapSDKVerificationTokenResponseToDomain(resp),
      ),
      catchError((err) => {
        this.showErrorToast(err, 'generateToken');
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'recovery-request-code')),
    );
  }

  validateRecoveryCode(
    payload: ValidateRecoveryCodeRequest,
  ): Observable<ValidateRecoveryCodeResponse> {
    this.appEvents.loaderVisiblePublish(true, 'recovery-validate-code');

    const normalizedEmail = payload.email.trim().toLowerCase();
    const sdkPayload: IamSdk.ValidateVerificationTokenRequest = {
      email: normalizedEmail,
      token: payload.token,
    };

    const source$: Observable<IamSdk.VerificationTokenValidationResponse> = this.shouldMock('validateVerificationToken')
      ? of(recoverySdkMock.validateVerificationToken(payload) as any).pipe(delay(450))
      : this.verificationTokensApi.validateVerificationToken(sdkPayload);

    return source$.pipe(
      map((resp: IamSdk.VerificationTokenValidationResponse) =>
        mapSDKVerificationTokenValidationToDomain(resp),
      ),
      catchError((err) => {
        this.showErrorToast(err, 'validateToken');
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'recovery-validate-code')),
    );
  }

  resetPassword(payload: ResetPasswordRequest): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'recovery-reset-password');

    const normalizedEmail = payload.email.trim().toLowerCase();
    const sdkPayload: IamSdk.ResetPasswordRequest = {
      email: normalizedEmail,
      newPassword: payload.newPassword,
      confirmPassword: payload.confirmPassword,
    };

    const source$: Observable<void> = this.shouldMock('resetPassword')
      ? of(recoverySdkMock.resetPassword(payload)).pipe(delay(450))
      : this.verificationTokensApi
          .resetPassword(sdkPayload)
          .pipe(map(() => void 0));

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err, 'resetPassword');
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'recovery-reset-password')),
    );
  }

  private showErrorToast(err: any, context: 'generateToken' | 'validateToken' | 'resetPassword') {
    if (!this.toast) return;

    const status = err?.status;
    const backendCode = err?.error?.code;

    const errorMessages: Record<string, Record<string, string>> = {
      generateToken: {
        'KLAB400': 'Solicitud inválida',
        'KLAB404': 'No se encontró un usuario con este correo',
        'KLAB429': 'Demasiadas solicitudes de verificación. Intenta más tarde',
        'KLAB500': 'Ocurrió un error inesperado',
      },
      validateToken: {
        'KLAB400': 'Solicitud inválida',
        'KLAB401': 'Código de verificación inválido o expirado',
        'KLAB404': 'No se encontró un usuario con este correo',
        'KLAB500': 'Ocurrió un error inesperado',
      },
      resetPassword: {
        'KLAB400': RESET_PASSWORD_POLICY_MESSAGE,
        'KLAB401': 'Token de verificación inválido o expirado',
        'KLAB404': 'No se encontró un usuario con este correo',
        'KLAB500': 'Ocurrió un error inesperado',
      },
    };

    const statusFallback: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado',
      404: 'No encontrado',
      429: 'Demasiadas solicitudes',
      500: 'Error interno del servidor',
    };

    // Una cuenta cesada o suspendida se explica sola con el texto que manda
    // IAM, y ademas debe ganarle al fallback por status: si cayera ahi, un
    // cesado leeria "No autorizado" y no sabria que su cuenta ya no existe.
    const displayMessage = isAccountDisabledError(err)
      ? accountDisabledMessage(err)
      : errorMessages[context]?.[backendCode] ||
      statusFallback[status] || err?.error?.message ||
      'Ocurrió un error inesperado';

    this.toast.show({
      data: {
        title: 'Advertencia',
        msg: displayMessage,
        direction: CoreUIToastDirection.topCenter,
        autoClose: false,
        closable: true,
      },
    });
  }

  private shouldMock(action: RecoveryMockAction): boolean {
    return Boolean(this.env?.useMocks || this.env?.mock?.passwordRecovery?.[action]);
  }
}
